import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { institutionSessionsQuery, institutionViewsQuery } from "./edu.queries";
import StatCard, {
  formatCountOrEmpty,
  formatDurationOrEmpty,
} from "../analytics/kpi/StatCard";

import type { JSX } from "react";

import type { AppState } from "@/src/reducers";

interface SessionAndViewAnalyticsProps {
  institutionUuid: string;
}

function SessionAndViewAnalytics({
  institutionUuid,
}: SessionAndViewAnalyticsProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-edu");
  const lang = useSelector((state: AppState) => state.variableReducer.lang);

  const sessionsResult = useQuery(
    institutionSessionsQuery(projectSlug, institutionUuid),
  );
  const viewsResult = useQuery(
    institutionViewsQuery(projectSlug, institutionUuid),
  );
  const sessions = sessionsResult.data;
  const views = viewsResult.data;

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
      <StatCard
        label={t("totalOfSessions")}
        query={sessionsResult}
        scope="asOfToday"
        value={formatCountOrEmpty(sessions?.total_sessions, lang)}
      />
      <StatCard
        label={t("sessionsThisMonth")}
        query={sessionsResult}
        value={formatCountOrEmpty(sessions?.current_month_sessions, lang)}
      />
      <StatCard
        label={t("totalOfViewing")}
        query={viewsResult}
        scope="asOfToday"
        value={formatDurationOrEmpty(views?.total_viewing_duration)}
      />
      <StatCard
        label={t("viewingHoursThisMonth")}
        query={viewsResult}
        value={formatDurationOrEmpty(views?.current_month_viewing_duration)}
      />
    </div>
  );
}

export default SessionAndViewAnalytics;
